import { createSlice } from "@reduxjs/toolkit";
import { castDraft } from "immer";

import {
  buildMetricTimelineKey,
  buildMetricsListScopeKey,
  initialMetricTimelineState,
  initialMetricsExplorerState,
  initialMetricsListState
} from "./helpers";
import { loadMetricTimeline, loadMetricsList } from "./thunks";

const metricsExplorerSlice = createSlice({
  name: "metricsExplorer",
  initialState: initialMetricsExplorerState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadMetricsList.pending, (state, action) => {
        const key = buildMetricsListScopeKey(
          action.meta.arg.projectId,
          action.meta.arg.environmentId,
          action.meta.arg.service
        );
        const prev = state.lists[key] ?? initialMetricsListState;
        state.lists[key] = {
          ...prev,
          status:
            prev.status === "idle" || prev.status === "loading"
              ? "loading"
              : "reloading",
          error: null
        };
      })
      .addCase(loadMetricsList.fulfilled, (state, action) => {
        const key = buildMetricsListScopeKey(
          action.meta.arg.projectId,
          action.meta.arg.environmentId,
          action.meta.arg.service
        );
        state.lists[key] = {
          status: "success",
          data: castDraft(action.payload.metrics),
          error: null
        };
      })
      .addCase(loadMetricsList.rejected, (state, action) => {
        // Switching service aborts the in-flight request; that is navigation,
        // not a failure, so leave the scope in its pending state.
        if (action.meta.aborted) return;
        const key = buildMetricsListScopeKey(
          action.meta.arg.projectId,
          action.meta.arg.environmentId,
          action.meta.arg.service
        );
        const message =
          action.payload?.error ?? action.error.message ?? "Unknown error";
        state.lists[key] = {
          ...(state.lists[key] ?? initialMetricsListState),
          status: "error",
          error: message
        };
      })
      .addCase(loadMetricTimeline.pending, (state, action) => {
        const key = buildMetricTimelineKey(
          action.meta.arg.metricHref,
          action.meta.arg.fromUnixSeconds,
          action.meta.arg.toUnixSeconds
        );
        const prev = state.timelines[key] ?? initialMetricTimelineState;
        state.timelines[key] = {
          ...prev,
          status:
            prev.status === "idle" || prev.status === "loading"
              ? "loading"
              : "reloading",
          error: null
        };
      })
      .addCase(loadMetricTimeline.fulfilled, (state, action) => {
        const key = buildMetricTimelineKey(
          action.meta.arg.metricHref,
          action.meta.arg.fromUnixSeconds,
          action.meta.arg.toUnixSeconds
        );
        state.timelines[key] = {
          status: "success",
          data: castDraft(action.payload),
          error: null
        };
      })
      .addCase(loadMetricTimeline.rejected, (state, action) => {
        if (action.meta.aborted) return;
        const key = buildMetricTimelineKey(
          action.meta.arg.metricHref,
          action.meta.arg.fromUnixSeconds,
          action.meta.arg.toUnixSeconds
        );
        const message =
          action.payload?.error ?? action.error.message ?? "Unknown error";
        state.timelines[key] = {
          ...(state.timelines[key] ?? initialMetricTimelineState),
          status: "error",
          error: message
        };
      });
  }
});

export default metricsExplorerSlice.reducer;
